import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Microphone } from '@phosphor-icons/react';
import { MoeChatDrawer } from './MoeChatDrawer';
import { useMoeVoice } from '../hooks/useMoeVoice';
import { useWakeWord } from '../hooks/useWakeWord';
import { API_URL } from '../config/api';
import { toast } from 'sonner';

/**
 * MoeButton — minimal, unobtrusive entry point to the Moe assistant.
 * Fixed top-right across every authenticated page. Tap to open chat drawer.
 * Long-press (desktop: 400ms hold, mobile: touch-and-hold) to record voice.
 *
 * Alpha-gated via /api/moe/alpha-status — renders nothing for non-alpha users.
 */
export const MoeButton = () => {
  const [isAlpha, setIsAlpha] = useState(null);
  const [chatOpen, setChatOpen] = useState(false);
  const [isPressing, setIsPressing] = useState(false);
  const [pendingBlob, setPendingBlob] = useState(null);
  const pressTimer = useRef(null);
  const longPressTriggered = useRef(false);
  const voice = useMoeVoice();
  const wakeWord = useWakeWord(() => {
    setChatOpen(true);
    voice.start().catch(() => {});
  });

  useEffect(() => {
    axios.get(`${API_URL}/api/moe/alpha-status`, { withCredentials: true })
      .then(r => setIsAlpha(r.data.is_alpha))
      .catch(() => setIsAlpha(false));
  }, []);

  if (!isAlpha) return null;

  const handlePressStart = (e) => {
    e.preventDefault();
    longPressTriggered.current = false;
    // 400ms threshold — shorter = accidental long-press on quick taps
    pressTimer.current = setTimeout(async () => {
      longPressTriggered.current = true;
      setIsPressing(true);
      try {
        await voice.start();
      } catch {
        toast.error('Microphone permission needed');
        setIsPressing(false);
      }
    }, 400);
  };

  const handlePressEnd = async (e) => {
    e?.preventDefault?.();
    if (pressTimer.current) clearTimeout(pressTimer.current);

    if (longPressTriggered.current) {
      setIsPressing(false);
      const blob = await voice.stop();
      if (blob && blob.size > 2000) {
        setPendingBlob(blob);
        setChatOpen(true);
      } else {
        setChatOpen(true);
      }
    } else {
      // Short tap → just open the drawer
      setChatOpen(true);
    }
  };

  const handleCancel = () => {
    if (pressTimer.current) clearTimeout(pressTimer.current);
    if (isPressing) {
      voice.stop();
      setIsPressing(false);
    }
  };

  return (
    <>
      <button
        data-testid="moe-button"
        onMouseDown={handlePressStart}
        onMouseUp={handlePressEnd}
        onMouseLeave={handleCancel}
        onTouchStart={handlePressStart}
        onTouchEnd={handlePressEnd}
        onTouchCancel={handleCancel}
        aria-label={isPressing ? 'Recording — release to send' : 'Ask Moe'}
        className={`fixed top-3 right-3 z-40 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold shadow-lg backdrop-blur transition-all select-none ${
          isPressing
            ? 'bg-red-500 text-white scale-110 shadow-red-500/50'
            : 'bg-emerald-600/90 hover:bg-emerald-600 text-white ring-1 ring-white/10'
        }`}
        style={{ WebkitTapHighlightColor: 'transparent' }}
      >
        <Microphone size={13} weight="fill" />
        <span>{isPressing ? 'listening…' : 'Ask Moe'}</span>
      </button>

      <MoeChatDrawer
        open={chatOpen}
        onOpenChange={(open) => {
          setChatOpen(open);
          if (!open) { voice.shutUp(); setPendingBlob(null); }
        }}
        onAnswering={() => {}}
        pendingBlob={pendingBlob}
        onBlobConsumed={() => setPendingBlob(null)}
        voice={voice}
        wakeWord={wakeWord}
      />
    </>
  );
};
